import React, { useState, useCallback, useEffect } from 'react';
import { useClientAccess } from '../hooks/useClientAccess';
import { useI18n } from '../contexts/I18nContext';
import { HardwareTypesTable } from '../components/deviceTypes/HardwareTypesTable';
import { ModelReferencesTable } from '../components/deviceTypes/ModelReferencesTable';
import { typesService } from '../services/typesService';
import { apiClient } from '../services/apiClient';
import '../styles/device-types.css';

type TabType = 'hardware' | 'modelrefs';

export const DeviceTypesPage: React.FC = () => {
  const { canEditDeviceTypes } = useClientAccess();
  const { formatTime, t } = useI18n();
  const [activeTab, setActiveTab] = useState<TabType>('hardware');
  const [refreshTypes, setRefreshTypes] = useState(0);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [hardwareCount, setHardwareCount] = useState(0);
  const [modelRefsCount, setModelRefsCount] = useState(0);

  // Fetch counts for tabs via /v2/hardware-types/stats (single call).
  // Previously this fan-out fetched all hw-types then sequentially
  // fetched model-refs for each — O(N) round-trips before the page
  // could finish loading. The stats endpoint already aggregates both.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get('/v2/hardware-types/stats');
        if (cancelled) return;
        setHardwareCount(res.total_types ?? 0);
        setModelRefsCount(res.total_model_refs ?? 0);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[DEVICE TYPES PAGE] stats fetch failed:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [refreshTypes]);

  const handleRefresh = useCallback(() => {
    typesService.clearCache();
    setRefreshTypes(prev => prev + 1);
    setLastUpdated(new Date());
  }, []);

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
  };

  return (
    <div className="page device-types-page">
      <div className="page-header">
        <div className="page-title">
          <h1>{t('deviceTypes.title')}</h1>
          <p>{t('deviceTypes.subtitle')}</p>
        </div>
        <div className="page-actions">
          <div className="last-updated">
            Last updated: {formatTime(lastUpdated)}
          </div>
          <button className="refresh-button" onClick={handleRefresh}>
            Refresh
          </button>
        </div>
      </div>

      <div className="device-types-page-header">
        <div className="page-tabs">
          <button
            className={`page-tab ${activeTab === 'hardware' ? 'active' : ''}`}
            data-testid="tab-hardware-types"
            onClick={() => handleTabChange('hardware')}
          >
            Hardware Types
            <span className="tab-count">{hardwareCount}</span>
          </button>
          <button
            className={`page-tab ${activeTab === 'modelrefs' ? 'active' : ''}`}
            data-testid="tab-model-references"
            onClick={() => handleTabChange('modelrefs')}
          >
            Model References
            <span className="tab-count">{modelRefsCount}</span>
          </button>
        </div>
      </div>

      <div className="page-content device-types-content">
        {activeTab === 'hardware' && (
          <HardwareTypesTable
            onTypeSelect={() => {}}
            refreshTrigger={refreshTypes}
            canEdit={canEditDeviceTypes}
          />
        )}
        {activeTab === 'modelrefs' && (
          <ModelReferencesTable
            refreshTrigger={refreshTypes}
            canEdit={canEditDeviceTypes}
          />
        )}
      </div>
    </div>
  );
};
